import { useEffect, useState } from 'react';
import toMoney from '~/common/masked/toMoney';
import { Button, Checkbox, Divider, Flex, Shimmer, Typography } from '~/ui/components';
import { Warning } from '~/ui/assets/icons';
import { useDrawer } from '~/hooks/useDrawer';
import { FluxoCobrancaScreens, TableNameTypes, useTracking } from '~/hooks/useTracking';
import { getLoggedUser } from '~/store/user/actions';
import { message } from 'antd';
import { getDividaByContractId } from '~/store/cobranca/actions';
import { useAwaitControl } from 'react-redux-await-control';
import { PaymentOptionResponse } from '~/typings/entities/Cobranca';
import { backoffice } from '~/services';
import usePersistTicketParams from '~/hooks/usePersistTicketParams';
import { StepTicket } from './StepTicket';
import { StepReview } from './StepReview';
import { CODIGO_CREDOR } from './constants';
import { useQuitacaoUnavailableGuard } from './QuitacaoUnavailableModal';

type Installment = {
  ccb: string;
  installmentNumber: number;
  dueDate: string;
  amount: number;
  interest: number;
  finalBalance: number;
  installmentId?: string;
  [key: string]: any;
};

export type PayOverdueInstallmentsProps = {
  document?: string;
  personName?: string;
  personId?: number;
  contractId?: string;
};

export function PayOverdueInstallments({ document, personName, personId, contractId }: PayOverdueInstallmentsProps) {
  const { setConfig } = useDrawer();
  const { handleTracking } = useTracking();
  const checkQuitacaoUnavailable = useQuitacaoUnavailableGuard();
  const getLoggedUserControl = useAwaitControl(getLoggedUser);
  const userId = getLoggedUserControl.result()?.id;

  const dividaControl = useAwaitControl(getDividaByContractId);
  // slot por contrato: sem actionId, abrir o drawer de um segundo contrato
  // pintava as parcelas do primeiro até a nova resposta chegar (review 4 #1975)
  const dividaResult = dividaControl.result(contractId);

  const codigoCliente = dividaResult?.clientCode || (personId ? String(personId) : undefined);

  const resolvedPersonId = personId ?? (dividaResult?.clientCode ? Number(dividaResult.clientCode) : undefined);
  const isDividaLoading = dividaControl.isRunning(contractId);

  const [isLoadingOffer, setIsLoadingOffer] = useState(false);
  const [ofertaOpcoes, setOfertaOpcoes] = useState<PaymentOptionResponse[] | undefined>();
  const [codigoSolicitacao, setCodigoSolicitacao] = useState<string | undefined>();

  const data: Installment[] = (dividaResult?.contracts || []).flatMap((contrato: any) =>
    (contrato.installments || []).map((p: any) => ({
      ccb: contrato.contractCode,
      installmentNumber: Number(p.installmentNumber),
      dueDate: p.dueDate,
      amount: p.paymentAmount,
      interest: p.interestAmount,
      finalBalance: p.principalAmount,
      installmentId: p.installmentId,
    })),
  );

  const { getPreFilledParam, persistTicketParams } = usePersistTicketParams(resolvedPersonId?.toString() || '');

  persistTicketParams();

  const [step, setStep] = useState<'select' | 'ticket' | 'review' | 'success'>('select');
  const [selected, setSelected] = useState<number[]>([]);
  const [outOfOrderWarning, setOutOfOrderWarning] = useState<string | null>(null);
  const [ticketId, setTicketId] = useState<string | undefined>();
  const [conversationId, setConversationId] = useState<string>(getPreFilledParam('conversationId') || '');

  const [dueDate, setDueDate] = useState<string | undefined>();

  useEffect(() => {
    if (contractId) {
      dividaControl.start({ contractId }, { actionId: contractId });
    }
  }, [contractId]);

  useEffect(() => {
    handleTracking(
      {
        event: FluxoCobrancaScreens.COBRANCA_DRAWER_ABERTO,
        contract_id: contractId,
        author_id: userId,
        timestamp: new Date().toISOString(),
        selected_installments: '',
        ticket_id: '',
        conversation_id: conversationId,
        payment_method_type: '',
      },
      TableNameTypes.BACKOFFICE_FLUXO_COBRANCA,
    );
  }, []);

  useEffect(() => {
    const backMap: Record<typeof step, (() => void) | undefined> = {
      select: undefined,
      ticket: () => setStep('select'),
      review: () => setStep('ticket'),
      success: undefined,
    };
    const titleMap: Record<typeof step, string> = {
      select: 'Pagar parcelas em atraso',
      ticket: 'Pagar parcelas em atraso',
      review: 'Revisar dados do boleto',
      success: 'Pagamento gerado',
    };
    setConfig('pay-overdue-installments', {
      title: titleMap[step],
      backButton: step === 'ticket' || step === 'review',
      onBackClick: backMap[step],
    });
  }, [step]);

  const sortedData = [...data].sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());

  const handleToggle = (installmentNumber: number) => {
    const isCurrentlySelected = selected.includes(installmentNumber);

    if (!isCurrentlySelected) {
      const installment = data.find((i) => i.installmentNumber === installmentNumber);
      const installmentDate = new Date(installment?.dueDate || '');

      const missingOlder = sortedData.find(
        (i) => new Date(i.dueDate).getTime() < installmentDate.getTime() && !selected.includes(i.installmentNumber),
      );

      if (missingOlder) {
        setOutOfOrderWarning(
          `Para pagar a parcela ${installmentNumber}, você deve obrigatoriamente selecionar a parcela ${missingOlder.installmentNumber} (mais antiga) primeiro.`,
        );
        return;
      }
    }

    setOutOfOrderWarning(null);
    setSelected((prev) =>
      isCurrentlySelected ? prev.filter((n) => n !== installmentNumber) : [...prev, installmentNumber],
    );
  };

  const selectedInstallments = data.filter((i) => selected.includes(i.installmentNumber));
  const totalAmount = selectedInstallments.reduce((acc, i) => acc + (i.amount || 0), 0);
  const selectedInstalmentsIdsAsString = selectedInstallments.map((i) => i.installmentId).join(',');

  const handleGoToTicket = () => {
    handleTracking(
      {
        event: FluxoCobrancaScreens.COBRANCA_PARCELAS_SELECIONADAS,
        contract_id: contractId,
        author_id: userId,
        timestamp: new Date().toISOString(),
        selected_installments: selectedInstalmentsIdsAsString,
        ticket_id: ticketId,
        conversation_id: conversationId,
        payment_method_type: 'PIX',
      },
      TableNameTypes.BACKOFFICE_FLUXO_COBRANCA,
    );
    setStep('ticket');
  };

  const handleFinalize = async () => {
    handleTracking(
      {
        event: FluxoCobrancaScreens.COBRANCA_CONFIRMACAO_DADOS,
        contract_id: contractId,
        author_id: userId,
        timestamp: new Date().toISOString(),
        selected_installments: selectedInstalmentsIdsAsString,
        ticket_id: ticketId,
        conversation_id: conversationId,
        payment_method_type: 'PIX',
      },
      TableNameTypes.BACKOFFICE_FLUXO_COBRANCA,
    );

    setIsLoadingOffer(true);
    try {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const dataPagamento = tomorrow.toISOString().split('T')[0];

      const { data: ofertaData } = await backoffice.post('/billing/offer/request', {
        chaveCliente: { codigoCredor: CODIGO_CREDOR, codigoCliente },
        tipoParcelamento: 'ParcelamentoFatura',
        valorEntrada: 0,
        descontoPercentual: 100,
        formaPagamento: 'PIX',
        dataPagamento,
        dataPagamentoParcela: dataPagamento,
        divida: (dividaResult?.contracts || [])
          .map((contrato: any) => ({
            codigoProduto: contrato.productCode,
            codigoContrato: contrato.contractCode,
            prestacoes: selectedInstallments
              .filter((i) => i.ccb === contrato.contractCode)
              .map((i) => ({
                numeroPrestacao: String(i.installmentNumber),
                dataVencimento: i.dueDate,
                idPrestacao: i.installmentId,
              })),
          }))
          .filter((d: any) => d.prestacoes.length > 0),
      });

      if (checkQuitacaoUnavailable(ofertaData)) return;

      const code = ofertaData?.paymentRequestCode;
      if (!code) throw new Error('Código de solicitação não retornado');
      setCodigoSolicitacao(code);

      const { data: opcoesData } = await backoffice.get(`/billing/offer/${CODIGO_CREDOR}/${codigoCliente}/${code}`);

      if (checkQuitacaoUnavailable(opcoesData)) return;

      setOfertaOpcoes(opcoesData?.paymentOptions);
      setStep('review');
    } catch {
      message.error('Erro ao solicitar oferta de pagamento');
    } finally {
      setIsLoadingOffer(false);
    }
  };

  const handleSuccess = () => {
    handleTracking(
      {
        event: FluxoCobrancaScreens.COBRANCA_PAGAMENTO_GERADO,
        contract_id: contractId,
        author_id: userId,
        timestamp: new Date().toISOString(),
        selected_installments: selectedInstalmentsIdsAsString,
        ticket_id: ticketId,
        conversation_id: conversationId,
        payment_method_type: 'PIX',
      },
      TableNameTypes.BACKOFFICE_FLUXO_COBRANCA,
    );
    setStep('success');
  };

  if (step === 'ticket') {
    return (
      <StepTicket
        personId={resolvedPersonId}
        contractId={contractId}
        ticketId={ticketId}
        conversationId={conversationId}
        dueDate={dueDate}
        onTicketChange={setTicketId}
        onConversationChange={setConversationId}
        onDueDateChange={setDueDate}
        onFinalize={handleFinalize}
        loading={isLoadingOffer}
      />
    );
  }

  if (step === 'review' || step === 'success') {
    return (
      <StepReview
        selectedInstallments={selectedInstallments}
        totalAmount={ofertaOpcoes?.[0]?.plans?.[0]?.amount ?? totalAmount}
        dueDate={ofertaOpcoes?.[0]?.paymentDate ?? dueDate}
        personName={personName}
        document={document}
        contractId={contractId}
        ticketId={ticketId}
        conversationId={conversationId}
        codigoCliente={codigoCliente}
        codigoSolicitacao={codigoSolicitacao}
        ofertaOpcoes={ofertaOpcoes}
        isSuccess={step === 'success'}
        onSuccess={handleSuccess}
      />
    );
  }

  return (
    <Flex direction="column" style={{ height: 'calc(100vh - 84px)' }}>
      <Flex direction="column" gap={16} style={{ flex: 1, overflow: 'auto', paddingTop: 16 }}>
        {outOfOrderWarning && (
          <Flex bg="#FFF7E6" radius={16} ph={12} pv={10} gap={8} align="center" justify="center">
            <Warning width={16} height={16} color="#F59E0B" style={{ flexShrink: 0 }} />
            <Typography type="bodySmall" color="feedback.warning.dark" style={{ textAlign: 'center' }}>
              {outOfOrderWarning}
            </Typography>
          </Flex>
        )}

        <Flex direction="column">
          <Flex ph={8} pv={8} gap={8} style={{ borderBottom: '1px solid #e8e8e8' }}>
            <Flex width={24} />
            {['PARCELA', 'VENCIMENTO', 'VALOR', 'JUROS', 'SALDO FINAL'].map((col) => (
              <Typography
                key={col}
                type="bodyXSmall"
                weight={700}
                color="element.secondary"
                style={{ flex: col === 'PARCELA' ? 1 : 2, whiteSpace: 'nowrap' }}
              >
                {col}
              </Typography>
            ))}
          </Flex>

          {isDividaLoading
            ? Array.from({ length: 4 }).map((_, i) => (
                <Flex key={i} ph={8} pv={10} gap={8} align="center">
                  <Shimmer width={24} height={16} br={4} />
                  <Shimmer height={16} br={4} />
                </Flex>
              ))
            : [...data]
                .sort((a, b) => a.installmentNumber - b.installmentNumber)
                .map((installment) => (
                  <Flex key={installment.installmentNumber} ph={8} pv={10} gap={8} align="center">
                    <Flex width={24} align="center" justify="center">
                      <Checkbox
                        name={`installment-${installment.installmentNumber}`}
                        checked={selected.includes(installment.installmentNumber)}
                        onChange={() => handleToggle(installment.installmentNumber)}
                      />
                    </Flex>
                    <Typography type="bodySmall" style={{ flex: 1 }}>
                      {installment.installmentNumber}
                    </Typography>
                    <Typography type="bodySmall" style={{ flex: 2 }}>
                      {installment.dueDate ? new Date(installment.dueDate).toLocaleDateString('pt-BR') : '-'}
                    </Typography>
                    <Typography type="bodySmall" weight={600} style={{ flex: 2 }}>
                      {toMoney(installment.amount)}
                    </Typography>
                    <Typography type="bodySmall" style={{ flex: 2 }}>
                      {toMoney(installment.interest)}
                    </Typography>
                    <Typography type="bodySmall" style={{ flex: 2 }}>
                      {toMoney(installment.finalBalance)}
                    </Typography>
                  </Flex>
                ))}
        </Flex>
      </Flex>

      <Flex direction="column" bg="background.primary">
        <Divider lineType="solid" />
        <Flex justify="flex-end" pv={16}>
          <Button variant="contained" disabled={selected.length === 0} onClick={handleGoToTicket}>
            Gerar Pagamento
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
}
